import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import get from 'lodash/get';
import uuidv4 from 'uuid/v4';

import Input from 'components/Form/Input';
import {
  Container,
  Row,
  Form,
  Col,
  Card,
  CardHeader,
  CardBody,
} from 'reactstrap';
import { Button } from 'components/Button';
import Validator from 'components/Validator/Validator';
import PasswordComplexityChecklist from 'components/PasswordComplexityChecklist/PasswordComplexityChecklist';

import {
  signUp, buildSignUpRequest, resolveErrorMessage, startAuthFlow,
} from 'utils/aws';
import { numberUnmask } from 'utils/masks';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import schema from './registrationConfirmSchema';

const RegistrationConfirm = (props) => {
  const { validator: { values, errors, setValues } } = props;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const email = get(location, 'state.email', '');
    if (!email || !get(location, 'state.ssn', '')) {
      navigate('/registration');
      return;
    }

    // Pre-populate the username with the email. The user can still change it.
    if (!values.username) {
      setValues({ ...values, username: email });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate]);

  const handleInputChange = (event) => {
    const {
      validator: { onChangeHandler },
    } = props;

    const value = event.target.value.trim();
    onChangeHandler(event.target.name, value);
  };

  const handleSubmitFrom = (data, e) => {
    e.preventDefault();
    const {
      validator: { validate },
    } = props;

    if (!validate(schema).isValid) {
      return;
    }

    const enteredUsername = `${data.username || ''}`;
    if (enteredUsername.length < 1 || enteredUsername.length > 128) {
      toast.error('Username must be between 1 and 128 characters.');
      return;
    }

    const enteredPassword = `${data.password || ''}`;
    if (enteredPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    const username = uuidv4();
    const email = get(location, 'state.email', '');
    const ssn = numberUnmask(get(location, 'state.ssn', ''));

    const { attributes, clientMetadata } = buildSignUpRequest(enteredUsername, email, ssn);

    const userCred = {
      payload: {
        username,
        password: enteredPassword,
        attributes,
        clientMetadata,
      },
    };

    signUp(userCred)
      .then((res) => {
        setIsLoading(false);
        const destination = get(res, 'nextStep.codeDeliveryDetails.destination', '');
        // Entering the email-verification step of registration: mark the auth
        // flow so the two-factor route guard allows the screen.
        startAuthFlow();
        navigate('/two-factor/verify-email', {
          state: {
            userName: username,
            // Human-readable username the user chose. The Cognito username is a
            // GUID, so this is carried through so the authenticator app can show
            // the real username (not the GUID) when the QR code is scanned.
            displayUsername: enteredUsername,
            password: enteredPassword,
            email: destination,
            ssn,
          },
        });
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(resolveErrorMessage(err));
      });
  };

  return (
    <div className="registration-page">
      <Container fluid>
        <Form>
          <Row className="align-items-center" style={{ minHeight: '500px' }}>
            <Col lg={{ size: 4, offset: 4 }} md={{ size: 6, offset: 3 }}>
              <Card>
                <CardHeader>User Registration</CardHeader>
                <CardBody>
                  <Row>
                    <Col>
                      <h5 className="mb-2 text-center">Create your username and password</h5>
                    </Col>
                  </Row>
                  <Row className="pt-3">
                    <Col>
                      <Input
                        name="username"
                        label="Username"
                        value={values.username}
                        onChange={handleInputChange}
                        hasError={!!errors.username}
                        hasValue
                        placeHolder="username"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.username}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Input
                        name="password"
                        type="password"
                        label="Password"
                        value={values.password}
                        onChange={handleInputChange}
                        hasError={!!errors.password}
                        hasValue
                        placeHolder="Password"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.password}
                        aria-describedby="password-requirements"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Input
                        name="confirmPassword"
                        label="Re-Enter Password"
                        onChange={handleInputChange}
                        value={values.confirmPassword}
                        hasError={!!errors.confirmPassword}
                        hasValue
                        placeHolder="Confirm Password"
                        isBadgeVisible={false}
                        isRequired
                        type="password"
                        errorMessage={errors.confirmPassword}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <PasswordComplexityChecklist
                        password={values.password || ''}
                        confirmPassword={values.confirmPassword || ''}
                      />
                    </Col>
                  </Row>
                  <Row className="pt-1">
                    <Col>
                      <Button
                        className="w-100"
                        onClick={handleSubmitFrom.bind(null, values)}
                        isLoading={isLoading}
                        color="primary"
                        type="submit"
                      >
                        Register
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

RegistrationConfirm.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator(schema))(RegistrationConfirm);
